/**
 * Scaffold Cleanup — Deterministic removal of OSS scaffold remnants
 *
 * After a full raft run, the OSS scaffold leaves files that contradict
 * the generated project. This tool removes/rewrites them.
 *
 * The file list is a hardcoded allowlist — not a glob. Only known scaffold
 * remnants are touched. This is a "Type B" mechanical operation.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import {
  existsSync,
  lstatSync,
  unlinkSync,
  readFileSync,
  writeFileSync,
  readdirSync,
  rmdirSync,
  mkdirSync,
} from 'fs';
import { join, dirname } from 'path';

// ---------------------------------------------------------------------------
// Constants — hardcoded scaffold remnant paths (allowlist, not glob)
// ---------------------------------------------------------------------------

/** Files to delete unconditionally if they exist */
const SCAFFOLD_FILES_TO_DELETE: readonly string[] = [
  'content/posts/getting-started.yaml',
  'content/posts/hello-world.yaml',
];

/**
 * Scaffold-default content fingerprints for pages/getting-started/content.yml.
 * If the file contains ANY of these strings it's an unmodified scaffold page —
 * safe to delete. If none match, assume user-authored content and preserve.
 */
const GETTING_STARTED_SCAFFOLD_FINGERPRINTS: readonly string[] = [
  'Welcome to your new Stackwright',
  'Petstore API',
  'Petstore',
];

/**
 * Extensions that indicate real user post files in content/posts/.
 * If any file with these extensions exists alongside _collection.yaml
 * (after scaffold posts are removed), the user has actual blog content —
 * preserve the collection file.
 */
const POST_FILE_EXTENSIONS: readonly string[] = ['.yaml', '.yml', '.md', '.mdx'];

/** The posts collection metadata file — safe to remove when no user posts exist */
const POSTS_COLLECTION_FILE = '_collection.yaml';

/** Directories to remove if empty after file deletion */
const SCAFFOLD_DIRS_TO_PRUNE: readonly string[] = [
  'pages/getting-started',
  'content/posts',
  // Don't prune 'content' — user may have other content directories
];

/** File to rewrite with theme-aware content */
const NOT_FOUND_PATH = 'app/not-found.tsx';

// ---------------------------------------------------------------------------
// Theme token reader
// ---------------------------------------------------------------------------

interface ThemeColors {
  background: string;
  foreground: string;
  primary: string;
  mutedForeground: string;
}

/** Neutral fallback when no theme artifact exists */
const FALLBACK_COLORS: ThemeColors = {
  background: '#ffffff',
  foreground: '#171717',
  primary: '#525252',
  mutedForeground: '#737373',
};

/**
 * Read theme colors from the pipeline's theme-tokens artifact.
 * Returns fallback colors if the artifact doesn't exist or is malformed.
 */
function readThemeColors(cwd: string): ThemeColors {
  const tokenPath = join(cwd, '.stackwright', 'artifacts', 'theme-tokens.json');

  if (!existsSync(tokenPath)) return { ...FALLBACK_COLORS };

  // Symlink guard
  const stat = lstatSync(tokenPath);
  if (stat.isSymbolicLink()) return { ...FALLBACK_COLORS };

  try {
    const raw = JSON.parse(readFileSync(tokenPath, 'utf-8'));
    const css = raw?.cssVariables ?? {};

    // CSS variables use HSL format like "222.2 84% 4.9%"
    // Convert to hsl() for use in the not-found template
    const toHsl = (hslValue: string | undefined): string | null => {
      if (!hslValue || typeof hslValue !== 'string') return null;
      return `hsl(${hslValue})`;
    };

    return {
      background: toHsl(css['--background']) ?? FALLBACK_COLORS.background,
      foreground: toHsl(css['--foreground']) ?? FALLBACK_COLORS.foreground,
      primary: toHsl(css['--primary']) ?? FALLBACK_COLORS.primary,
      mutedForeground: toHsl(css['--muted-foreground']) ?? FALLBACK_COLORS.mutedForeground,
    };
  } catch {
    return { ...FALLBACK_COLORS };
  }
}

// ---------------------------------------------------------------------------
// Not-found page template
// ---------------------------------------------------------------------------

function generateNotFoundPage(colors: ThemeColors): string {
  return `export default function NotFound() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '${colors.background}',
        color: '${colors.foreground}',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: '4rem', fontWeight: 700, margin: 0, color: '${colors.primary}' }}>
        404
      </h1>
      <p style={{ fontSize: '1.25rem', color: '${colors.mutedForeground}', marginTop: '0.5rem' }}>
        Page not found
      </p>
      <a
        href="/"
        style={{
          marginTop: '2rem',
          padding: '0.75rem 1.5rem',
          backgroundColor: '${colors.primary}',
          color: '${colors.background}',
          borderRadius: '0.5rem',
          textDecoration: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
        }}
      >
        Go Home
      </a>
    </div>
  );
}
`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Check if a path is safe to operate on (not a symlink) */
function isSafePath(fullPath: string): boolean {
  if (!existsSync(fullPath)) return true; // non-existent is safe (for writes)
  const stat = lstatSync(fullPath);
  return !stat.isSymbolicLink();
}

/** Check if a directory is empty */
function isDirEmpty(dirPath: string): boolean {
  if (!existsSync(dirPath)) return false;
  const stat = lstatSync(dirPath);
  if (!stat.isDirectory()) return false;
  return readdirSync(dirPath).length === 0;
}

// ---------------------------------------------------------------------------
// Handler (exported for direct testing — no MCP server needed)
// ---------------------------------------------------------------------------

export interface CleanupScaffoldResult {
  deleted: string[];
  rewritten: string[];
  skipped: string[];
  errors: string[];
  prunedDirs: string[];
  /** Paths the safety guard intentionally preserved (user-modified content) */
  cleanupWarnings: string[];
}

export function handleCleanupScaffold(_cwd?: string): { text: string; isError: boolean } {
  const cwd = _cwd ?? process.cwd();

  const result: CleanupScaffoldResult = {
    deleted: [],
    rewritten: [],
    skipped: [],
    errors: [],
    prunedDirs: [],
    cleanupWarnings: [],
  };

  // 1. Delete known scaffold files
  for (const relPath of SCAFFOLD_FILES_TO_DELETE) {
    const fullPath = join(cwd, relPath);

    if (!existsSync(fullPath)) {
      result.skipped.push(relPath);
      continue;
    }

    // Symlink guard
    if (!isSafePath(fullPath)) {
      result.errors.push(`Refusing to delete symlink: ${relPath}`);
      continue;
    }

    try {
      unlinkSync(fullPath);
      result.deleted.push(relPath);
    } catch (err) {
      result.errors.push(`Failed to delete ${relPath}: ${String(err)}`);
    }
  }

  // 1b. Conditionally delete pages/getting-started/content.yml
  //     Only removed when it still contains scaffold-default content.
  //     User-authored getting-started pages are preserved with a warning.
  {
    const relPath = 'pages/getting-started/content.yml';
    const fullPath = join(cwd, relPath);

    if (!existsSync(fullPath)) {
      result.skipped.push(relPath);
    } else if (!isSafePath(fullPath)) {
      result.errors.push(`Refusing to delete symlink: ${relPath}`);
    } else {
      const content = readFileSync(fullPath, 'utf-8');
      const isScaffoldDefault = GETTING_STARTED_SCAFFOLD_FINGERPRINTS.some((marker) =>
        content.includes(marker)
      );

      if (isScaffoldDefault) {
        try {
          unlinkSync(fullPath);
          result.deleted.push(relPath);
        } catch (err) {
          result.errors.push(`Failed to delete ${relPath}: ${String(err)}`);
        }
      } else {
        result.cleanupWarnings.push(
          `pages/getting-started/content.yml preserved — no scaffold fingerprint found (user-modified content)`
        );
      }
    }
  }

  // 1c. Conditionally delete content/posts/_collection.yaml
  //     Only removed when no real user post files exist alongside it.
  //     Scaffold posts (getting-started.yaml, hello-world.yaml) were handled
  //     in step 1, so any remaining files are user-authored.
  {
    const relPath = `content/posts/${POSTS_COLLECTION_FILE}`;
    const fullPath = join(cwd, relPath);
    const postsDir = join(cwd, 'content/posts');

    if (!existsSync(fullPath)) {
      result.skipped.push(relPath);
    } else if (!isSafePath(fullPath)) {
      result.errors.push(`Refusing to delete symlink: ${relPath}`);
    } else if (!existsSync(postsDir)) {
      // Parent directory gone already — skip
      result.skipped.push(relPath);
    } else {
      // Check for user post files (anything other than _collection.yaml itself)
      const userPostFiles = readdirSync(postsDir).filter(
        (f) => f !== POSTS_COLLECTION_FILE && POST_FILE_EXTENSIONS.some((ext) => f.endsWith(ext))
      );

      if (userPostFiles.length === 0) {
        try {
          unlinkSync(fullPath);
          result.deleted.push(relPath);
        } catch (err) {
          result.errors.push(`Failed to delete ${relPath}: ${String(err)}`);
        }
      } else {
        result.cleanupWarnings.push(
          `content/posts/_collection.yaml preserved — ${userPostFiles.length} user post file(s) exist: ${userPostFiles.slice(0, 3).join(', ')}`
        );
      }
    }
  }

  // 2. Prune empty directories
  for (const relDir of SCAFFOLD_DIRS_TO_PRUNE) {
    const fullDir = join(cwd, relDir);

    if (!existsSync(fullDir)) continue;

    // Symlink guard on directory
    if (!isSafePath(fullDir)) {
      result.errors.push(`Refusing to prune symlink directory: ${relDir}`);
      continue;
    }

    if (isDirEmpty(fullDir)) {
      try {
        rmdirSync(fullDir);
        result.prunedDirs.push(relDir);
      } catch (err) {
        result.errors.push(`Failed to prune directory ${relDir}: ${String(err)}`);
      }
    }
  }

  // 3. Rewrite not-found.tsx with theme colors
  {
    const fullPath = join(cwd, NOT_FOUND_PATH);

    if (!existsSync(fullPath)) {
      result.skipped.push(NOT_FOUND_PATH);
    } else if (!isSafePath(fullPath)) {
      result.errors.push(`Refusing to rewrite symlink: ${NOT_FOUND_PATH}`);
    } else {
      try {
        const colors = readThemeColors(cwd);
        const content = generateNotFoundPage(colors);

        // Ensure directory exists (defensive)
        const dir = dirname(fullPath);
        mkdirSync(dir, { recursive: true });

        writeFileSync(fullPath, content, 'utf-8');
        result.rewritten.push(NOT_FOUND_PATH);
      } catch (err) {
        result.errors.push(`Failed to rewrite ${NOT_FOUND_PATH}: ${String(err)}`);
      }
    }
  }

  return {
    text: JSON.stringify(result),
    isError: false,
  };
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerScaffoldCleanupTools(server: McpServer): void {
  const DESC =
    'Deterministic scaffold remnant cleanup — removes stale OSS scaffold files after raft run.';

  server.tool(
    'stackwright_pro_cleanup_scaffold',
    `Remove/rewrite stale scaffold files (blog posts, hardcoded not-found page) after the raft pipeline completes. ${DESC}`,
    {},
    async () => {
      const r = handleCleanupScaffold();
      return {
        content: [{ type: 'text' as const, text: r.text }],
        isError: r.isError,
      };
    }
  );
}
